'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

const supabase = createClient();

interface Preferenze {
  id?: string;
  tema: string;
  lingua: string;
  dimensione_testo: number;
  alto_contrasto: boolean;
  riduzione_movimento: boolean;
  colore_primario: string;
  nome_azienda: string;
  notifiche_push_attive: boolean;
  widget_dashboard: string[];
}

interface NotificaPush {
  id: string;
  titolo: string;
  corpo: string;
  url_azione: string;
  stato: string;
  priorita: string;
  created_at: string;
}

interface CodaSync {
  id: string;
  operazione: string;
  tabella: string;
  stato: string;
  tentativi: number;
  created_at: string;
}

type TabType = 'accessibilita' | 'tema' | 'notifiche' | 'lingua' | 'offline' | 'pwa';

const WIDGET_OPTIONS = [
  { id: 'allarmi', label: 'Allarmi Attivi' },
  { id: 'ticket', label: 'Ticket Aperti' },
  { id: 'dispositivi', label: 'Dispositivi Offline' },
  { id: 'banda', label: 'Utilizzo Banda' },
  { id: 'scadenze', label: 'Scadenze Imminenti' },
  { id: 'anomalie', label: 'Anomalie Rete' },
];

const DEFAULT_PREFERENZE: Preferenze = {
  tema: 'scuro',
  lingua: 'it',
  dimensione_testo: 100,
  alto_contrasto: false,
  riduzione_movimento: false,
  colore_primario: '#0ea5e9',
  nome_azienda: 'GestioneLab',
  notifiche_push_attive: false,
  widget_dashboard: ['allarmi', 'ticket', 'dispositivi', 'banda'],
};

export default function ImpostazioniContent() {
  const [activeTab, setActiveTab] = useState<TabType>('accessibilita');
  const [preferenze, setPreferenze] = useState<Preferenze>(DEFAULT_PREFERENZE);
  const [notifiche, setNotifiche] = useState<NotificaPush[]>([]);
  const [codaSync, setCodaSync] = useState<CodaSync[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [isOnline, setIsOnline] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setIsOnline(navigator.onLine);
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [prefRes, notifRes, syncRes] = await Promise.all([
        supabase.from('preferenze_utente').select('*').eq('utente_id', user.id).single(),
        supabase.from('notifiche_push').select('*').eq('utente_id', user.id).order('created_at', { ascending: false }).limit(20),
        supabase.from('coda_sincronizzazione').select('*').eq('utente_id', user.id).order('created_at', { ascending: false }).limit(10),
      ]);

      if (prefRes.data) {
        setPreferenze({
          id: prefRes.data.id,
          tema: prefRes.data.tema || 'scuro',
          lingua: prefRes.data.lingua || 'it',
          dimensione_testo: prefRes.data.dimensione_testo || 100,
          alto_contrasto: prefRes.data.alto_contrasto || false,
          riduzione_movimento: prefRes.data.riduzione_movimento || false,
          colore_primario: prefRes.data.colore_primario || '#0ea5e9',
          nome_azienda: prefRes.data.nome_azienda || 'GestioneLab',
          notifiche_push_attive: prefRes.data.notifiche_push_attive || false,
          widget_dashboard: prefRes.data.widget_dashboard || ['allarmi', 'ticket', 'dispositivi', 'banda'],
        });
      }
      if (notifRes.data) setNotifiche(notifRes.data);
      if (syncRes.data) setCodaSync(syncRes.data);
    } catch (e: any) {
      setError(e?.message || 'Errore nel caricamento preferenze');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const savePreferenze = async () => {
    setSaving(true);
    setError('');
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Utente non autenticato');

      const payload = {
        utente_id: user.id,
        tema: preferenze.tema,
        lingua: preferenze.lingua,
        dimensione_testo: preferenze.dimensione_testo,
        alto_contrasto: preferenze.alto_contrasto,
        riduzione_movimento: preferenze.riduzione_movimento,
        colore_primario: preferenze.colore_primario,
        nome_azienda: preferenze.nome_azienda,
        notifiche_push_attive: preferenze.notifiche_push_attive,
        widget_dashboard: preferenze.widget_dashboard,
        updated_at: new Date().toISOString(),
      };

      if (preferenze.id) {
        const { error: updateErr } = await supabase.from('preferenze_utente').update(payload).eq('id', preferenze.id);
        if (updateErr) throw updateErr;
      } else {
        const { data, error: insertErr } = await supabase.from('preferenze_utente').insert(payload).select().single();
        if (insertErr) throw insertErr;
        if (data) setPreferenze(prev => ({ ...prev, id: data.id }));
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e: any) {
      setError(e?.message || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const markNotificaLetta = async (id: string) => {
    await supabase.from('notifiche_push').update({ stato: 'letta', letta_at: new Date().toISOString() }).eq('id', id);
    setNotifiche(prev => prev.map(n => n.id === id ? { ...n, stato: 'letta' } : n));
  };

  const toggleWidget = (widgetId: string) => {
    setPreferenze(prev => ({
      ...prev,
      widget_dashboard: prev.widget_dashboard.includes(widgetId)
        ? prev.widget_dashboard.filter(w => w !== widgetId)
        : [...prev.widget_dashboard, widgetId],
    }));
  };

  const tabs: { id: TabType; label: string; icon: string }[] = [
    { id: 'accessibilita', label: 'Accessibilità', icon: 'EyeIcon' },
    { id: 'tema', label: 'Tema e Layout', icon: 'PaintBrushIcon' },
    { id: 'notifiche', label: 'Notifiche Push', icon: 'BellIcon' },
    { id: 'lingua', label: 'Lingua', icon: 'LanguageIcon' },
    { id: 'offline', label: 'Offline & Sync', icon: 'CloudArrowDownIcon' },
    { id: 'pwa', label: 'App Mobile (PWA)', icon: 'DevicePhoneMobileIcon' },
  ];

  const prioritaColor: Record<string, string> = {
    critica: 'text-red-400',
    alta: 'text-orange-400',
    normale: 'text-sky-400',
    bassa: 'text-gray-400',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Impostazioni</h1>
          <p className="text-text-secondary text-sm mt-1">Accessibilità, tema, notifiche, lingua e modalità offline</p>
        </div>
        <button
          onClick={savePreferenze}
          disabled={saving}
          className="flex items-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : saved ? (
            <Icon name="CheckIcon" size={16} />
          ) : (
            <Icon name="CloudArrowUpIcon" size={16} />
          )}
          {saving ? 'Salvataggio...' : saved ? 'Salvato!' : 'Salva su DB'}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-surface border border-border rounded-xl p-1 flex-wrap">
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-colors ${activeTab === t.id ? 'bg-sky-600 text-white' : 'text-secondary-color hover:text-white'}`}
          >
            <Icon name={t.icon as any} size={14} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-surface border border-border rounded-xl p-6">
        {activeTab === 'tema' && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-white">Tema e Layout</h2>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-2">Tema</label>
              <div className="flex gap-3">
                {['scuro', 'chiaro', 'sistema'].map(t => (
                  <button
                    key={t}
                    onClick={() => setPreferenze(p => ({ ...p, tema: t }))}
                    className={`px-4 py-2 rounded-xl text-sm font-semibold border transition-colors capitalize ${preferenze.tema === t ? 'bg-sky-600 border-sky-500 text-white' : 'border-border text-secondary-color hover:text-white'}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-color mt-2">Il tema viene salvato nel database e applicato su tutti i dispositivi.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-2">Colore Primario</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={preferenze.colore_primario}
                  onChange={e => setPreferenze(p => ({ ...p, colore_primario: e.target.value }))}
                  className="w-12 h-10 rounded-lg border border-border cursor-pointer bg-transparent"
                />
                <span className="text-sm text-secondary-color font-mono">{preferenze.colore_primario}</span>
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-3">Nome Azienda</label>
              <input
                type="text"
                value={preferenze.nome_azienda}
                onChange={e => setPreferenze(p => ({ ...p, nome_azienda: e.target.value }))}
                className="w-full max-w-xs bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-3">Widget Dashboard</label>
              <div className="grid grid-cols-2 gap-2">
                {WIDGET_OPTIONS.map(w => (
                  <button
                    key={w.id}
                    onClick={() => toggleWidget(w.id)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm border transition-colors ${preferenze.widget_dashboard.includes(w.id) ? 'bg-sky-600/20 border-sky-500/40 text-sky-400' : 'border-border text-secondary-color hover:text-white'}`}
                  >
                    <div className={`w-3 h-3 rounded-full border-2 ${preferenze.widget_dashboard.includes(w.id) ? 'bg-sky-400 border-sky-400' : 'border-muted-color'}`} />
                    {w.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'accessibilita' && (
          <div className="space-y-6">
            <h2 className="text-base font-bold text-white">Accessibilità</h2>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-2">Dimensione Testo: {preferenze.dimensione_testo}%</label>
              <input
                type="range"
                min={80}
                max={150}
                step={10}
                value={preferenze.dimensione_testo}
                onChange={e => setPreferenze(p => ({ ...p, dimensione_testo: Number(e.target.value) }))}
                className="w-full max-w-xs"
              />
            </div>
            <div className="space-y-3">
              {[
                { key: 'alto_contrasto', label: 'Alto Contrasto', desc: 'Aumenta il contrasto per migliorare la leggibilità' },
                { key: 'riduzione_movimento', label: 'Riduzione Movimento', desc: 'Disabilita animazioni e transizioni' },
              ].map(opt => (
                <div key={opt.key} className="flex items-center justify-between p-4 bg-surface-elevated border border-border rounded-xl">
                  <div>
                    <p className="text-sm font-semibold text-white">{opt.label}</p>
                    <p className="text-xs text-muted-color">{opt.desc}</p>
                  </div>
                  <button
                    onClick={() => setPreferenze(p => ({ ...p, [opt.key]: !p[opt.key as keyof Preferenze] }))}
                    className={`w-11 h-6 rounded-full transition-colors ${(preferenze as any)[opt.key] ? 'bg-sky-500' : 'bg-surface'} border border-border relative`}
                  >
                    <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${(preferenze as any)[opt.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'lingua' && (
          <div className="space-y-4">
            <h2 className="text-base font-bold text-white">Lingua</h2>
            <div>
              <label className="block text-sm font-semibold text-secondary-color mb-2">Lingua interfaccia</label>
              <select
                value={preferenze.lingua}
                onChange={e => setPreferenze(p => ({ ...p, lingua: e.target.value }))}
                className="bg-surface-elevated border border-border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500"
              >
                <option value="it">🇮🇹 Italiano</option>
                <option value="en">🇬🇧 English</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'notifiche' && (
          <div className="space-y-4">
            <h2 className="text-base font-bold text-white">Notifiche Push</h2>
            <div className="flex items-center justify-between p-4 bg-surface-elevated border border-border rounded-xl">
              <div>
                <p className="text-sm font-semibold text-white">Notifiche Push Attive</p>
                <p className="text-xs text-muted-color">Ricevi notifiche push nel browser</p>
              </div>
              <button
                onClick={() => setPreferenze(p => ({ ...p, notifiche_push_attive: !p.notifiche_push_attive }))}
                className={`w-11 h-6 rounded-full transition-colors ${preferenze.notifiche_push_attive ? 'bg-sky-500' : 'bg-surface'} border border-border relative`}
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 transition-transform ${preferenze.notifiche_push_attive ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {notifiche.length === 0 ? (
              <div className="text-center py-8 text-muted-color">
                <Icon name="BellSlashIcon" size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessuna notifica push ricevuta</p>
              </div>
            ) : (
              <div className="space-y-2">
                {notifiche.map(n => (
                  <div key={n.id} className={`p-3 bg-surface-elevated border border-border rounded-xl ${n.stato === 'non_letta' ? 'border-sky-500/30' : 'border-border'}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className={`text-sm font-semibold ${prioritaColor[n.priorita] || 'text-white'}`}>{n.titolo}</p>
                        <p className="text-xs text-muted-color mt-0.5">{n.corpo}</p>
                      </div>
                      {n.stato === 'non_letta' && (
                        <button onClick={() => markNotificaLetta(n.id)} className="text-xs text-sky-400 hover:text-sky-300 whitespace-nowrap">
                          Segna letta
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'offline' && (
          <div className="space-y-4">
            <h2 className="text-base font-bold text-white">Offline & Sincronizzazione</h2>
            <div className={`flex items-center gap-3 p-4 rounded-xl border ${isOnline ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
              <div className={`w-3 h-3 rounded-full ${isOnline ? 'bg-green-400' : 'bg-red-400'}`} />
              <p className={`text-sm font-semibold ${isOnline ? 'text-green-400' : 'text-red-400'}`}>
                {isOnline ? 'Connesso' : 'Offline'}
              </p>
            </div>
            {codaSync.length === 0 ? (
              <div className="text-center py-8 text-muted-color">
                <Icon name="CloudArrowUpIcon" size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">Nessuna operazione in coda</p>
              </div>
            ) : (
              <div className="space-y-2">
                {codaSync.map(s => (
                  <div key={s.id} className="p-3 bg-surface-elevated border border-border rounded-xl">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-white capitalize">{s.operazione} — {s.tabella}</p>
                        <p className="text-xs text-muted-color">Tentativi: {s.tentativi}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${s.stato === 'completato' ? 'bg-green-500/20 text-green-400' : s.stato === 'fallito' ? 'bg-red-500/20 text-red-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {s.stato}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'pwa' && (
          <div className="space-y-4">
            <h2 className="text-base font-bold text-white">App Mobile (PWA)</h2>
            <div className="bg-sky-500/10 border border-sky-500/30 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Icon name="DevicePhoneMobileIcon" size={20} className="text-sky-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-white mb-1">Installa GestioneLab sul tuo dispositivo</p>
                  <p className="text-xs text-muted-color">
                    Su iOS: apri Safari → condividi → &quot;Aggiungi a schermata Home&quot;.<br />
                    Su Android: apri Chrome → menu → &quot;Aggiungi a schermata Home&quot;.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
